import React, {useCallback, useContext, useEffect, useState} from "react";
import {Box, Paper, Tab, Tabs, useTheme} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSetRecoilState} from "recoil";
import {AxiosError} from "axios";
import SectionHeader from "../../../util/component/SectionHeader";
import {IDmsTabs} from "./models";
import InfoTab from "./tabs-section/tabs/InfoTab";
import AssociationInfo from "./tabs-section/tabs/AssociationInfo";
import {useParams} from "react-router-dom";
import { getMobilityStoreById } from "../search-dms/dmsSearch.actions";
import {additionalInfoState, associationInfoState, documentsDmsState,
    eParkingState, financialInfoState, generalInfoState, posInfoState,
    proposalInfoState, representativeInfoState, keylessInfoState, keylessAreaState} from "../search-dms/state";
import DocumentRepositoryDms from "./tabs-section/tabs/DocumentRepositoryDms";
import ContentBody from "../common/content-body/ContentBody";
import OpenCloseTimeframe from "./tabs-section/tabs/OpenCloseTimeframe";
import {setSnackbar} from "../../../util/state/globalAction";
import {snackbarTypes} from "../../../util/state/globalVariables";
import {GlobalContext} from "../../../util/state/GlobalContextProvider";
import Keyless from "./tabs-section/tabs/Keyless";

const DmsDetail = () => {
    const theme = useTheme();
    const params = useParams(); // params.id
    const {t} = useTranslation(['network', 'translation']);
    const {dispatchGlobalData} = useContext(GlobalContext);

    const setGeneralInfo = useSetRecoilState(generalInfoState);
    const setAdditionalInfo = useSetRecoilState(additionalInfoState);
    const setProposalInfo = useSetRecoilState(proposalInfoState);
    const setEparking = useSetRecoilState(eParkingState);
    const setRepresentativeInfo = useSetRecoilState(representativeInfoState);
    const setPosInfo = useSetRecoilState(posInfoState);
    const setFinancialInfo = useSetRecoilState(financialInfoState);
    const setAssociationInfo = useSetRecoilState(associationInfoState);
    const setDocumentDms = useSetRecoilState(documentsDmsState);
    const setKeylessInfo = useSetRecoilState(keylessInfoState);
    const setKeylessArea= useSetRecoilState(keylessAreaState);

    // Use State
    const [tab, setTab] = useState<IDmsTabs>(IDmsTabs.INFO);

    // Use Callback
    const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    }, []);

    useEffect(() => {
        if (params.id !== undefined) {
            let reqParameters = {
                msId: params.id
            }
            getMobilityStoreById(reqParameters)
                .then( (res:any) => {
                    setGeneralInfo(res?.mobilityStore?.generalInfo);
                    setAdditionalInfo(res?.mobilityStore?.additionalInfo);
                    setProposalInfo(res?.mobilityStore?.proposalInfo);
                    setEparking(res?.mobilityStore?.eParking);
                    setPosInfo(res?.mobilityStore?.posInfo);
                    setFinancialInfo(res?.mobilityStore.financialInfo);
                    setRepresentativeInfo(res?.mobilityStore?.representativeInfo);
                    setAssociationInfo(res?.mobilityStore?.associationInfo);
                    setDocumentDms(res?.mobilityStore?.documents);
                    setKeylessInfo(res?.mobilityStore?.keylessInfo);
                    setKeylessArea(res?.mobilityStore?.keylessArea[0]);
                })
                .catch((err: AxiosError) => {
                    dispatchGlobalData({
                        type: setSnackbar,
                        payload: {
                            snackbar: {
                                message: t('error.tryLater'),
                                type: snackbarTypes.error,
                                show: true,
                                autoClose: true,
                            }
                        }
                    });
                });
        }
    },[])

    return(
        <>
            <SectionHeader title={t('dmsDetail.title')} />
            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                    <Box>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tab} onChange={handleChangeTab}>
                                <Tab label={t("dmsDetail.tab.info")}/>
                                <Tab label={t("dmsDetail.tab.association")}/>
                                <Tab label={t("dmsDetail.tab.documents")}/>
                                <Tab label={t("dmsDetail.tab.timeframe")}/>
                                <Tab label={t("dmsDetail.tab.keyless")}/>
                            </Tabs>
                        </Box>
                        <Box py={3}>
                            {tab === IDmsTabs.INFO &&
                                <InfoTab/>
                            }
                            {tab === IDmsTabs.ASSOCIATION_INFO &&
                                <AssociationInfo/>
                            }
                            {tab === IDmsTabs.DOCUMENT_REPOSITORY &&
                                <DocumentRepositoryDms/>
                            }
                            {tab === IDmsTabs.TIMEFRAME_INFO &&
                                <OpenCloseTimeframe/>
                            }
                            {tab === IDmsTabs.KEYLESS &&
                                <Keyless/>
                            }
                        </Box>
                    </Box>
                </Paper>


            </ContentBody>

        </>
    )
}

export default DmsDetail;